import React, { useEffect, useRef, useState } from "react";
import "./AppDevelopmentProcess.css";

const stages = [
  {
    name: "SPARK",
    description:
      "Turn an initial idea into a clear product vision by understanding the problem, users, goals, and opportunity.",
  },
  {
    name: "SHAPE",
    description:
      "Transform the vision into a structured product concept with defined features, user journeys, requirements, and a clear roadmap.",
  },
  {
    name: "FORGE",
    description:
      "Build the application using modern technologies, scalable architecture, clean code, APIs, databases, and secure development practices.",
  },
  {
    name: "TEST",
    description:
      "Challenge the application through functional, performance, security, compatibility, and real-world testing to ensure reliability.",
  },
  {
    name: "AWAKEN",
    description:
      "Bring the completed application to life through deployment, integration, configuration, and preparation for real users.",
  },
  {
    name: "EVOLVE",
    description:
      "Continuously monitor, improve, optimize, scale, and introduce new capabilities as the product and business grow.",
  },
];

function MethodologyConnections({ activeStep }) {
  const activeClass = activeStep ? `is-step-${activeStep}` : "";

  return (
    <>
      <svg
        className={`methodology-connections methodology-connections-desktop ${activeClass}`}
        viewBox="0 0 1200 200"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {stages.slice(0, -1).map((stage, index) => (
          <path
            key={stage.name}
            className={`methodology-connection connection-to-${index + 2}`}
            d={`M ${100 + index * 200} 100 H ${300 + index * 200}`}
          />
        ))}
      </svg>
      <svg
        className={`methodology-connections methodology-connections-tablet ${activeClass}`}
        viewBox="0 0 900 534"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path className="methodology-connection connection-to-2" d="M 150 126 H 450" />
        <path className="methodology-connection connection-to-3" d="M 450 126 H 750" />
        <path className="methodology-connection connection-to-4" d="M 750 126 C 750 246 150 288 150 408" />
        <path className="methodology-connection connection-to-5" d="M 150 408 H 450" />
        <path className="methodology-connection connection-to-6" d="M 450 408 H 750" />
      </svg>
      <svg
        className={`methodology-connections methodology-connections-mobile ${activeClass}`}
        viewBox="0 0 400 660"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path className="methodology-connection connection-to-2" d="M 100 100 H 300" />
        <path className="methodology-connection connection-to-3" d="M 300 100 C 300 190 100 210 100 320" />
        <path className="methodology-connection connection-to-4" d="M 100 320 H 300" />
        <path className="methodology-connection connection-to-5" d="M 300 320 C 300 410 100 430 100 540" />
        <path className="methodology-connection connection-to-6" d="M 100 540 H 300" />
      </svg>
    </>
  );
}

export default function AppDevelopmentProcess() {
  const sectionRef = useRef(null);
  const [activeStep, setActiveStep] = useState(null);
  const [pinnedStep, setPinnedStep] = useState(null);
  const [isReplaying, setIsReplaying] = useState(false);

  useEffect(() => {
    if (pinnedStep === null) return undefined;

    const clearPinnedStep = (event) => {
      if (!sectionRef.current?.contains(event.target)) {
        setPinnedStep(null);
        setActiveStep(null);
        setIsReplaying(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPinnedStep(null);
        setActiveStep(null);
        setIsReplaying(false);
      }
    };

    document.addEventListener("pointerdown", clearPinnedStep);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", clearPinnedStep);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pinnedStep]);

  const togglePinnedStep = (index) => {
    const nextStep = pinnedStep === index ? null : index;
    setPinnedStep(nextStep);
    setActiveStep(nextStep);
  };

  return (
    <section
      ref={sectionRef}
      className="app-methodology section-pad"
      id="app-development-process"
      aria-labelledby="app-methodology-title"
      data-active-step={activeStep === null ? "" : activeStep + 1}
      onPointerEnter={(event) => {
        if (event.pointerType !== "touch") setIsReplaying(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== "touch") setIsReplaying(false);
      }}
    >
      <div className="app-methodology-inner">
        <header className="app-methodology-heading">
          <h2 id="app-methodology-title">Our methodology, built to evolve</h2>
          {/*<h2 id="app-methodology-title">FROM SPARK TO EVOLUTION</h2>*/}
          <p className="app-methodology-intro">
            A structured approach to transforming ideas into powerful, reliable,
            and continuously evolving digital products. From discovery and
            planning to design, development, testing, and launch, each stage has
            a clear purpose and builds on the last. We keep teams aligned through
            practical decisions, dependable delivery, and feedback that helps
            the product improve as needs evolve.
          </p>
        </header>

        <div className={`methodology-journey${isReplaying ? " is-replaying" : ""}`}>
          <MethodologyConnections activeStep={activeStep === null ? null : activeStep + 1} />
          <ol className="methodology-stages">
            {stages.map((stage, index) => {
              const isActive = activeStep === index;
              const isPinned = pinnedStep === index;

              return (
                <li
                  className={`methodology-stage${isActive ? " is-active" : ""}`}
                  key={stage.name}
                  onPointerEnter={(event) => {
                    if (event.pointerType !== "touch") {
                      setActiveStep(index);
                      setIsReplaying(true);
                    }
                  }}
                  onPointerLeave={(event) => {
                    if (event.pointerType !== "touch") setActiveStep(pinnedStep);
                  }}
                  onFocus={() => setActiveStep(index)}
                  onBlur={() => setActiveStep(pinnedStep)}
                >
                  <button
                    className="methodology-node"
                    type="button"
                    aria-label={`${stage.name}. ${stage.description}`}
                    aria-pressed={isPinned}
                    onClick={() => {
                      togglePinnedStep(index);
                      setIsReplaying(true);
                    }}
                  >
                    <svg
                      className="methodology-node-rings"
                      viewBox="0 0 200 200"
                      aria-hidden="true"
                    >
                      <circle className="methodology-node-ring ring-outer" cx="100" cy="100" r="96" />
                      <circle className="methodology-node-ring ring-inner" cx="100" cy="100" r="88" />
                    </svg>
                    <span className="methodology-node-content">
                      <span className="methodology-node-name">{stage.name}</span>
                      <span className="methodology-node-description">{stage.description}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
